import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  MessageCircle,
  Phone,
  MapPin,
  ArrowRight,
  Sparkles,
  Layers,
  Users,
  BadgeEuro,
  Banknote,
  Ticket,
  Search,
  Clock,
  CalendarDays,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { CONTACT, IMAGES, LOGO, EVENT_FORMATS, API_BASE, formatEventDate, SORTIMENT } from "@/content";
import { useSettings } from "@/hooks/useSettings";
import { Seo } from "@/components/Seo";
const marqueeItems = [
  "Pokémon TCG",
  "One Piece TCG",
  "Viele weitere TCGs",
  "Anime-Figuren",
  "Sealed Produkte",
  "Einzelkarten",
  "Booster Packs",
  "Trade Days",
  "Karten-Ankauf",
  "Community",
];

export default function Home() {
  const { hours } = useSettings();
  const [events, setEvents] = useState([]);

  useEffect(() => {
    axios
      .get(`${API_BASE}/events`)
      .then((res) => setEvents(Array.isArray(res.data) ? res.data.slice(0, 3) : []))
      .catch(() => setEvents([]));
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="Anime & TCG Store Köln | Pokémon, One Piece & Karten-Ankauf"
        description="Shibuya Anime & TCG Store in Köln: Pokémon, One Piece, Yu-Gi-Oh!, Anime-Figuren, Zubehör, Einzelkarten, Events und Karten-Ankauf vor Ort. Zülpicher Str. 357, 50935 Köln."
        path="/"
        keywords="Anime Shop Köln, TCG Shop Köln, Pokémon Karten kaufen Köln, One Piece Karten kaufen Köln, Yu-Gi-Oh Karten kaufen Köln, Sammelkarten kaufen Köln, Trading Card Store Köln, Card Shop Köln, Anime Figuren Köln, Pokémon Store Köln"
      />
      {/* ============ HERO ============ */}
      <section
        data-testid="home-hero"
        className="relative flex min-h-[92vh] items-center overflow-hidden border-b border-border"
      >
        {/* Background artwork – Sasuke mit animiertem Sharingan */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="cover-frame cover-zoom">
            <img
              src={IMAGES.heroArt}
              alt=""
              aria-hidden="true"
              className="h-full w-full"
              loading="eager"
            />
            {/* Sharingan-Auge Glow (Position: 60% / 32.6% im Bild) */}
            <div aria-hidden="true" data-testid="sharingan-effect">
              <div className="sharingan-glow outer" style={{ left: "60%", top: "32.6%", transform: "translate(-50%, -50%)" }} />
              <div className="sharingan-glow" style={{ left: "60%", top: "32.6%" }} />
              <div className="sharingan-flash" style={{ left: "60%", top: "32.6%" }} />
            </div>
          </div>
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, hsl(20 14% 4.5% / 0.94) 0%, hsl(20 14% 4.5% / 0.8) 38%, hsl(18 30% 8% / 0.35) 70%, hsl(20 20% 5% / 0.3) 100%)",
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, hsl(20 14% 4.5% / 0.5) 0%, transparent 30%, transparent 60%, hsl(20 14% 4.5%) 100%)",
            }}
          />
          <div className="hero-glow absolute inset-0" />
          <div className="bg-grid absolute inset-0" />
        </div>

        {/* Neon-Kanji Schild rechts (wie ein Anime-Leuchtschild) */}
        <div
          aria-hidden="true"
          className="neon-flicker pointer-events-none absolute right-6 top-1/2 z-10 hidden -translate-y-1/2 select-none lg:block"
        >
          <p className="neon-kanji kanji-vertical text-5xl">渋谷ストア</p>
        </div>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-8 top-10 select-none font-jp text-[140px] font-black leading-none text-white/[0.04] md:text-[240px]"
        >
          渋谷
        </div>

        <div className="relative z-10 mx-auto w-full max-w-6xl px-4 py-20 sm:px-6">
          <motion.div
            className="max-w-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <img
              src={LOGO}
              alt="Shibuya Anime & TCG Store Logo"
              data-testid="home-hero-logo"
              className="h-20 w-auto drop-shadow-[0_8px_30px_rgba(0,0,0,0.6)] md:h-28"
            />

            <Badge
              variant="outline"
              className="mt-8 rounded-full border-primary/40 bg-primary/10 px-4 py-1.5 font-mono-ui text-[11px] uppercase tracking-widest text-primary backdrop-blur-sm"
            >
              <MapPin className="mr-1.5 h-3 w-3" />
              Köln · Zülpicherstraße 357
            </Badge>

            <h1 className="mt-5 font-display text-5xl uppercase leading-[0.95] tracking-[0.04em] sm:text-6xl lg:text-7xl">
              Anime &amp; TCG Store
              <br />
              <span className="neon-text">in Köln</span>
            </h1>

            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Dein Store für Pokémon, One Piece, Yu-Gi-Oh! &amp; viele weitere
              TCGs, Anime-Figuren, Zubehör, Einzelkarten und Community-Events –
              mitten in Köln. Komm vorbei, prüfe alles vor Ort und nimm es direkt mit.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                asChild
                size="lg"
                data-testid="home-hero-phone-button"
                className="h-12 rounded-xl bg-primary px-6 font-medium text-primary-foreground shadow-[0_10px_30px_hsl(22_100%_50%/0.35)] transition-shadow hover:shadow-[0_14px_44px_hsl(22_100%_50%/0.5)]"
              >
                <a href={CONTACT.phoneHref}>
                  <Phone className="mr-2 h-5 w-5" /> Jetzt anrufen
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                data-testid="home-hero-whatsapp-button"
                className="h-12 rounded-xl border-primary/30 bg-background/40 px-6 backdrop-blur-sm hover:bg-primary/10"
              >
                <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp
                </a>
              </Button>
              <a
                href={CONTACT.mapsHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-hero-maps-link"
                className="link-slide flex items-center gap-1.5 px-2 py-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                <MapPin className="h-4 w-4 text-primary" /> Route planen
              </a>
            </div>

            <div className="mt-12 grid max-w-md grid-cols-3 gap-4">
              {[
                { big: "TCG", small: "Riesige Auswahl – Fokus: Pokémon & One Piece" },
                { big: "100%", small: "Vor Ort: anfassen, prüfen, mitnehmen" },
                { big: "€", small: "Ankauf mit Sofort-Auszahlung" },
              ].map((stat) => (
                <div key={stat.small}>
                  <p className="font-display text-3xl text-primary">{stat.big}</p>
                  <p className="mt-1 font-mono-ui text-[10px] uppercase leading-snug tracking-wider text-muted-foreground">
                    {stat.small}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div
          className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="h-6 w-6 text-muted-foreground" />
        </motion.div>

        {/* Manga SFX */}
        <span
          aria-hidden="true"
          data-testid="hero-sfx"
          className="sfx pointer-events-none absolute right-[16%] top-[24%] z-10 hidden text-5xl text-accent md:block lg:text-7xl"
          style={{ "--sfx-rot": "-12deg" }}
        >
          ドン!
        </span>
      </section>

      {/* ============ MARQUEE ============ */}
      <div
        data-testid="home-marquee"
        className="marquee-wrap overflow-hidden border-b border-border bg-card py-3.5"
      >
        <div className="marquee-track flex w-max items-center gap-8">
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <span
              key={`${item}-${i}`}
              className="flex items-center gap-8 whitespace-nowrap font-mono-ui text-xs uppercase tracking-[0.2em] text-muted-foreground"
            >
              {item}
              <span className="font-jp text-accent">★</span>
            </span>
          ))}
        </div>
      </div>

      {/* ============ INTRO / SEO TEXT ============ */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6 md:py-16">
          <Reveal>
            <h2 className="font-display text-3xl uppercase tracking-[0.04em] sm:text-4xl">
              Willkommen im Shibuya <span className="neon-text">Anime &amp; TCG Store</span>
            </h2>
            <div className="mt-5 space-y-4 text-sm leading-relaxed text-muted-foreground md:text-base">
              <p>
                Der Shibuya Anime &amp; TCG Store ist dein Anlaufpunkt für Trading
                Card Games und Anime in Köln. Du findest uns auf der Zülpicher
                Straße 357 in Köln-Sülz – mit <strong className="font-semibold text-foreground">Pokémon</strong> und{" "}
                <strong className="font-semibold text-foreground">One Piece</strong> als Schwerpunkt, ergänzt um Yu-Gi-Oh!,
                hochwertiges TCG-Zubehör, Anime-Figuren und Merch.
              </p>
              <p>
                Bei uns bekommst du Booster, Displays, Sealed-Produkte und
                Einzelkarten zum Anfassen, Prüfen und direkt Mitnehmen – ohne
                Versandrisiko. Du möchtest Karten verkaufen? Wir kaufen Sammlungen
                und Einzelkarten fair an und zahlen sofort aus. Und bei unseren
                Trade Days und Events triffst du die Kölner Community.
              </p>
            </div>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {SORTIMENT.map((s) => (
                <Link
                  key={s.to}
                  to={s.to}
                  data-testid={`home-sortiment-${s.to}`}
                  className="group inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                >
                  {s.label}
                  <ArrowRight className="h-3.5 w-3.5 text-primary transition-transform group-hover:translate-x-0.5" />
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ 3 PILLARS BENTO ============ */}
      <section className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
        <div aria-hidden="true" className="halftone halftone-fade pointer-events-none absolute inset-x-0 top-0 h-48" />
        <Reveal>
          <div className="flex items-center gap-3">
            <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary">
              01 / Was dich erwartet
            </p>
            <span aria-hidden="true" className="neon-kanji-orange text-sm">三つの世界</span>
          </div>
          <h2 className="mt-3 font-display text-4xl uppercase tracking-[0.04em] sm:text-5xl">
            Drei Welten. <span className="neon-text">Ein Store.</span>
          </h2>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-12">
          <Reveal className="md:col-span-7" delay={0.05}>
            <div
              data-testid="pillar-tcg-card"
              className="group neon-card noise manga-frame relative h-full overflow-hidden rounded-2xl border border-border bg-card"
            >
              <div className="halftone-corner" aria-hidden="true" />
              <div className="relative h-64 overflow-hidden md:h-80">
                <img
                  src={IMAGES.tcgCards}
                  alt="Pokémon, One Piece, Magic und Yu-Gi-Oh Trading Cards"
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                />
                <div className="holo-foil" aria-hidden="true" data-testid="tcg-holo-foil" />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent" />
                <span aria-hidden="true" className="neon-kanji absolute right-4 top-4 text-2xl">カード</span>
              </div>
              <div className="relative z-10 p-6">
                <div className="flex items-center gap-2">
                  <Layers className="h-5 w-5 text-primary" />
                  <p className="font-display text-2xl uppercase tracking-wide">
                    TCG – Karten &amp; Produkte
                  </p>
                </div>
                <p className="mt-2 max-w-lg text-sm leading-relaxed text-muted-foreground">
                  Von Pokémon über One Piece bis zu vielen weiteren TCGs: große
                  Auswahl an Einzelkarten, Boosterpacks, Sealed-Produkten und
                  Zubehör. Am stärksten vertreten: Pokémon &amp; One Piece.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["Pokémon", "One Piece", "Weitere TCGs", "Sealed", "Zubehör"].map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="rounded-full bg-secondary font-mono-ui text-[10px] uppercase tracking-wider text-muted-foreground"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5">
                  <Link to="/pokemon-karten-koeln" data-testid="pillar-link-pokemon" className="link-slide text-sm font-medium text-primary">Pokémon Karten Köln</Link>
                  <Link to="/one-piece-karten-koeln" data-testid="pillar-link-onepiece" className="link-slide text-sm font-medium text-primary">One Piece Karten Köln</Link>
                  <Link to="/yugioh-karten-koeln" data-testid="pillar-link-yugioh" className="link-slide text-sm font-medium text-primary">Yu-Gi-Oh! Karten Köln</Link>
                </div>
              </div>
            </div>
          </Reveal>

          <div className="flex flex-col gap-6 md:col-span-5">
            <Reveal delay={0.1}>
              <div
                data-testid="pillar-merch-card"
                className="group neon-card noise manga-frame relative overflow-hidden rounded-2xl border border-border bg-card"
              >
                <div className="halftone-corner" aria-hidden="true" />
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={IMAGES.figures}
                    alt="Anime Sammelfiguren im Grandista-Stil"
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent" />
                  <span aria-hidden="true" className="neon-kanji absolute right-4 top-4 text-2xl">フィギュア</span>
                </div>
                <div className="relative z-10 p-6">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-accent" />
                    <p className="font-display text-2xl uppercase tracking-wide">
                      Anime-Merch &amp; Figuren
                    </p>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    Von detailreichen Figuren – z. B. Bandai Grandista – bis zu
                    stylischem Merch deiner Lieblings-Charaktere. Handverlesen
                    und regelmäßig neu im Regal.
                  </p>
                  <Link to="/anime-figuren-koeln" data-testid="pillar-link-figuren" className="link-slide mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                    Anime Figuren in Köln <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div
                data-testid="pillar-community-card"
                className="group neon-card noise manga-frame relative flex-1 overflow-hidden rounded-2xl border border-border bg-card"
              >
                <div className="halftone-corner" aria-hidden="true" />
                <div className="relative h-32 overflow-hidden">
                  <img
                    src={IMAGES.community}
                    alt="Leute tauschen Karten am Tisch"
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent" />
                  <span aria-hidden="true" className="neon-kanji absolute right-4 top-4 text-2xl">仲間</span>
                </div>
                <div className="relative z-10 p-6">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    <p className="font-display text-2xl uppercase tracking-wide">
                      Treffen, Tauschen &amp; Spielen
                    </p>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    Triff andere Fans, tausche Karten am Tisch und genieße die
                    Community-Atmosphäre direkt im Store.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ EVENTS TEASER ============ */}
      <section className="relative overflow-hidden border-y border-border bg-card/50">
        <div aria-hidden="true" className="speedlines pointer-events-none absolute inset-0 opacity-60" />
        <div aria-hidden="true" data-testid="events-action-burst" className="action-burst pointer-events-none absolute -left-32 top-0 h-[460px] w-[460px] opacity-70" />
        <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-accent">
                    02 / Events
                  </p>
                  <span aria-hidden="true" className="neon-kanji text-sm">イベント</span>
                  <span aria-hidden="true" data-testid="events-sfx" className="sfx text-3xl text-primary" style={{ "--sfx-rot": "7deg" }}>バン!</span>
                </div>
                <h2 className="mt-3 font-display text-4xl uppercase tracking-[0.04em] sm:text-5xl">
                  Trade Days &amp; <span className="neon-text-red">mehr.</span>
                </h2>
              </div>
              <Button
                asChild
                variant="outline"
                data-testid="home-events-cta"
                className="h-11 rounded-xl border-primary/30 bg-transparent px-5 hover:bg-primary/10"
              >
                <Link to="/events">
                  Alle Events <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Echte anstehende Termine (aus der API) */}
          {events.length > 0 && (
            <div data-testid="home-upcoming-events" className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
              {events.map((event, i) => (
                <Reveal key={event.id} delay={i * 0.06}>
                  <Link to="/events" className="block h-full">
                    <div className="neon-card noise relative h-full overflow-hidden rounded-2xl border border-primary/30 bg-card p-6">
                      <div className="relative z-10">
                        <div className="flex items-center justify-between">
                          <Badge className="rounded-full bg-primary/15 font-mono-ui text-[10px] uppercase tracking-wider text-primary">
                            {event.tag}
                          </Badge>
                          <span className="font-display text-lg text-accent">
                            {formatEventDate(event.date)}
                          </span>
                        </div>
                        <p className="mt-3 font-display text-2xl uppercase leading-tight tracking-wide">
                          {event.title}
                        </p>
                        {event.time_text && (
                          <p className="mt-1 flex items-center gap-1.5 font-mono-ui text-[11px] uppercase tracking-wider text-muted-foreground">
                            <Clock className="h-3.5 w-3.5" /> {event.time_text}
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}

          {/* Event-Formate */}
          <div
            data-testid="home-events-teaser"
            className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
          >
            {EVENT_FORMATS.map((event, i) => (
              <Reveal key={event.id} delay={i * 0.06}>
                <Link to="/events" className="block h-full">
                  <div className="neon-card noise relative h-full overflow-hidden rounded-2xl border border-border bg-card p-6">
                    <span aria-hidden="true" className="neon-kanji-orange absolute right-4 top-4 text-lg opacity-60">
                      {event.kanji}
                    </span>
                    <div className="relative z-10">
                      <Badge
                        className={`rounded-full font-mono-ui text-[10px] uppercase tracking-wider ${
                          event.accent === "accent"
                            ? "bg-accent/15 text-accent"
                            : "bg-primary/15 text-primary"
                        }`}
                      >
                        {event.tag}
                      </Badge>
                      <p className="mt-4 font-display text-2xl uppercase leading-tight tracking-wide">
                        {event.title}
                      </p>
                      <p className="mt-1.5 flex items-center gap-1.5 font-mono-ui text-[11px] uppercase tracking-wider text-muted-foreground">
                        <CalendarDays className="h-3.5 w-3.5" /> Event-Format
                      </p>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ANKAUF TEASER ============ */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
        <Reveal>
          <div
            data-testid="home-ankauf-teaser"
            className="noise relative overflow-hidden rounded-2xl border border-border bg-card"
          >
            <div className="cta-glow pointer-events-none absolute inset-0" />
            <span aria-hidden="true" className="neon-kanji-orange absolute right-6 top-5 z-10 text-3xl opacity-70">買取</span>
            <div className="relative z-10 grid grid-cols-1 gap-10 p-6 md:p-10 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <Badge
                  variant="outline"
                  className="rounded-full border-accent/40 bg-accent/10 px-4 py-1.5 font-mono-ui text-[11px] uppercase tracking-widest text-accent"
                >
                  <BadgeEuro className="mr-1.5 h-3 w-3" />
                  Professioneller Ankauf
                </Badge>
                <h2 className="mt-4 font-display text-4xl uppercase tracking-[0.04em] sm:text-5xl">
                  Karten zu Cash.
                  <br />
                  <span className="neon-text">Direkt vor Ort.</span>
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">
                  Wir kaufen Sammlungen, Sealed-Produkte und Pokémon-Karten –
                  egal ob alt oder neu – sowie aktuelle One Piece-Karten.
                  Bewertung und Auszahlung sofort, fair und transparent.
                </p>
                <Button
                  asChild
                  size="lg"
                  data-testid="home-ankauf-cta"
                  className="mt-6 h-12 rounded-xl bg-primary px-6 font-medium text-primary-foreground shadow-[0_10px_30px_hsl(22_100%_50%/0.35)] transition-shadow hover:shadow-[0_14px_44px_hsl(22_100%_50%/0.5)]"
                >
                  <Link to="/ankauf">
                    Zum Ankauf <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:col-span-7">
                {[
                  {
                    icon: Search,
                    step: "01",
                    title: "Bewertung",
                    text: "Wir prüfen Zustand & Marktwert gemeinsam vor Ort.",
                  },
                  {
                    icon: Banknote,
                    step: "02",
                    title: "Bar / Überweisung",
                    text: "60–90% des Wertes, sofort ausgezahlt.",
                  },
                  {
                    icon: Ticket,
                    step: "03",
                    title: "Store-Gutschein",
                    text: "70–100% des Wertes, flexibel einlösbar.",
                  },
                ].map((item) => (
                  <div
                    key={item.step}
                    className="rounded-xl border border-border bg-background/60 p-5"
                  >
                    <item.icon className="h-5 w-5 text-primary" />
                    <p className="mt-3 font-mono-ui text-[10px] uppercase tracking-widest text-muted-foreground">
                      Schritt {item.step}
                    </p>
                    <p className="mt-1 font-display text-xl uppercase tracking-wide">
                      {item.title}
                    </p>
                    <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ============ LOCATION ============ */}
      <section
        data-testid="home-location"
        className="border-t border-border bg-card/50"
      >
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <div className="flex items-center gap-3">
                <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary">
                  03 / Besuch uns
                </p>
                <span aria-hidden="true" className="neon-kanji-orange text-sm">ケルン市</span>
              </div>
              <h2 className="mt-3 font-display text-4xl uppercase tracking-[0.04em] sm:text-5xl">
                Mitten in <span className="neon-text">Köln.</span>
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">
                Du findest uns auf der Zülpicher Straße – komm einfach während
                unserer Öffnungszeiten vorbei. Karten anfassen, Figuren bestaunen,
                Leute treffen.
              </p>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div>
                    <p className="text-sm font-medium">{CONTACT.addressStreet}</p>
                    <p className="text-sm text-muted-foreground">{CONTACT.addressCity}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
                  <Clock className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div className="w-full">
                    <p className="text-sm font-medium">Öffnungszeiten</p>
                    <div data-testid="home-opening-hours" className="mt-1.5 space-y-1">
                      {hours.map((row) => (
                        <div key={row.days} className="flex justify-between gap-4 text-sm text-muted-foreground">
                          <span>{row.days}</span>
                          <span className={row.time === "Geschlossen" ? "" : "text-foreground"}>{row.time}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  asChild
                  data-testid="home-location-phone-button"
                  className="h-11 rounded-xl bg-primary px-5 font-medium text-primary-foreground"
                >
                  <a href={CONTACT.phoneHref}>
                    <Phone className="mr-2 h-4 w-4" /> {CONTACT.phoneDisplay}
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  data-testid="home-location-maps-button"
                  className="h-11 rounded-xl border-primary/30 bg-transparent px-5 hover:bg-primary/10"
                >
                  <a href={CONTACT.mapsHref} target="_blank" rel="noopener noreferrer">
                    <MapPin className="mr-2 h-4 w-4" /> Route in Google Maps
                  </a>
                </Button>
              </div>
            </Reveal>

            <Reveal className="lg:col-span-7" delay={0.1}>
              <div className="h-full min-h-[320px] overflow-hidden rounded-2xl border border-border shadow-xl shadow-black/40">
                <iframe
                  title="Shibuya Store Köln – Standort"
                  data-testid="home-map-embed"
                  src={CONTACT.mapsEmbed}
                  className="h-full min-h-[320px] w-full"
                  style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) saturate(0.5)" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
